import * as React from 'react';
import {
  type CanvasDocumentPhysicalResources,
  type CanvasDocumentPresentationSnapshot,
  type CanvasDocumentScope,
} from './canvasDocumentScope';


const CanvasDocumentScopeContext = React.createContext<CanvasDocumentScope | null>(null);

export function CanvasDocumentScopeProvider({
  scope,
  children,
}: {
  scope: CanvasDocumentScope;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <CanvasDocumentScopeContext.Provider value={scope}>
      {children}
    </CanvasDocumentScopeContext.Provider>
  );
}

export function useCanvasDocumentScope(): CanvasDocumentScope {
  const scope = React.useContext(CanvasDocumentScopeContext);
  if (!scope) {
    throw new Error(
      'Canvas document scope context is missing: render the resident canvas under '
      + 'CanvasDocumentScopeProvider, after acquiring the document of its conversation '
      + '(canvasDocumentRuntime.acquireForConversation). A component rendered outside that '
      + 'provider has no document to address.',
    );
  }
  return scope;
}

export function useCanvasDocumentPhysicalResources(): CanvasDocumentPhysicalResources {
  const scope = useCanvasDocumentScope();
  return React.useSyncExternalStore(
    React.useCallback((listener) => scope.subscribePhysical(listener), [scope]),
    React.useCallback(() => scope.getPhysicalResources(), [scope]),
    React.useCallback(() => scope.getPhysicalResources(), [scope]),
  );
}

/**
 * Le document de CETTE conversation, observé directement (#1333).
 *
 * Le panneau lisait auparavant une projection globale du store, arbitrée à
 * chaque rendu contre l'instantané du scope. Il n'y a plus d'arbitrage parce
 * qu'il n'y a plus deux valeurs : un résident parqué et un résident visible
 * lisent chacun leur propre document, sans qu'aucune sélection d'écran ne
 * décide qui a le droit de lire quoi.
 */
export function useCanvasDocumentPresentation(): CanvasDocumentPresentationSnapshot {
  const scope = useCanvasDocumentScope();
  const lire = React.useCallback(() => scope.getPresentationOrEmpty(), [scope]);
  return React.useSyncExternalStore(
    React.useCallback((listener) => scope.subscribePresentation(listener), [scope]),
    lire,
    lire,
  );
}

/**
 * L'époque du montage que ce scope incarne — LA seule identité de remontage de
 * l'éditeur (#1337).
 *
 * `RedactionPanel` en compose la clé React de `RedactionCanvas` : chaque
 * remontage runtime (remplacement, page vierge, admission d'un travail qui
 * remplace le document, réhydratation) alloue une époque, donc reconstruit
 * l'éditeur ; rien d'autre ne le fait plus. Un nombre, et non l'instantané
 * entier : `subscribeSnapshot` notifie aussi chaque révision, et un primitif
 * inchangé ne redemande aucun rendu.
 */
export function useCanvasDocumentMountEpoch(): number {
  const scope = useCanvasDocumentScope();
  const lire = React.useCallback(() => scope.getSnapshot().mountEpoch, [scope]);
  return React.useSyncExternalStore(
    React.useCallback((listener) => scope.subscribeSnapshot(() => listener()), [scope]),
    lire,
    lire,
  );
}
